import {
  useCompany,
  useCurrentUserResource,
  useFilter,
  useGetCompanyTeamOptions,
  useGetResourcesPaginated,
} from '@dispatch/shared/hooks';
import {
  ComplexLossDataResponse,
  ComplexLossFilters,
  MultiSelectOption,
  defaultFilters,
} from '@dispatch/shared/types';
import { ComplexLossTreeView } from '@dispatch/web/features/complex-loss';
import { Filter, PageComponent } from '@dispatch/web/ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useGetComplexLossData } from '@dispatch/shared/hooks';
import { Outlet } from 'react-router-dom';
import { isEmpty } from 'lodash';

export const ComplexLossPage = () => {
  const [apiFilters, setApiFilters] =
    useState<ComplexLossFilters>(defaultFilters);
  const { filters: dynamicFilters, setFilters: setDynamicFilters } =
    useFilter();
  const { teams } = useCompany();
  const { isAdjuster, resourceId } = useCurrentUserResource();
  const [complexLossData, setComplexLossData] = useState<unknown>({
    //required to use any here because of type used in complexLossDataQuery
    resources: [],
    events: [],
    assignments: [],
  });

  // initialize default filters
  useEffect(() => {
    if (isAdjuster) {
      setDynamicFilters({
        ...defaultFilters,
        complexResources: [
          {
            title: resourceId, // title can be anything as this filter is hidden as adjuster
            value: resourceId,
          },
        ],
      });
    } else {
      setDynamicFilters(defaultFilters);
    }

    // Clear filters when leaving Complex Loss to prevent them persisting to other views
    return () => {
      setDynamicFilters({});
    };
  }, [isAdjuster, resourceId, setDynamicFilters]);

  const [debouncedFilters, setDebouncedFilters] = React.useState<{
    dynamicFilters: ComplexLossFilters;
    apiFilters: ComplexLossFilters;
  }>({
    dynamicFilters: {} as ComplexLossFilters,
    apiFilters: {} as ComplexLossFilters,
  });

  useDebounce(() => setDebouncedFilters({ dynamicFilters, apiFilters }), 500, [
    dynamicFilters,
    apiFilters,
  ]);

  const complexLossDataQuery = useGetComplexLossData(
    debouncedFilters.dynamicFilters,
    debouncedFilters.apiFilters,
    !isEmpty(debouncedFilters.dynamicFilters) &&
      !isEmpty(debouncedFilters.apiFilters)
  );

  useEffect(() => {
    if (
      complexLossDataQuery?.status === 'success' &&
      complexLossDataQuery?.data
    ) {
      setComplexLossData(complexLossDataQuery.data);
    }
  }, [
    complexLossDataQuery.data,
    complexLossDataQuery?.status,
    setComplexLossData,
  ]);

  const [
    complexLossResourceFilterDebouncedValue,
    setComplexLossResourceFilterDebouncedValue,
  ] = useState('');
  const [complexLossResourceFilterInput, setComplexLossResourceFilterInput] =
    useState<string>('');

  const { companyTeams } = useGetCompanyTeamOptions(teams || []);

  useDebounce(
    () => {
      setComplexLossResourceFilterDebouncedValue(
        complexLossResourceFilterInput
      );
    },
    1000,
    [complexLossResourceFilterInput]
  );

  const onSearch = useCallback(
    (value: string) => {
      setApiFilters({ ...apiFilters, claimNumber: value });
    },
    [apiFilters, setApiFilters]
  );

  useEffect(() => {
    // handles search from url query params when navigating from other pages
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('claimNumber') && !apiFilters?.claimNumber) {
      onSearch(queryParams.get('claimNumber') || '');
    }
  }, [apiFilters?.claimNumber, onSearch, setApiFilters]);

  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: complexLossResourceFilterDebouncedValue,
      pageSize: 25, //search only first 25
    },
    enabled: !!complexLossResourceFilterDebouncedValue,
  });

  const mappedFilteredResources = useMemo(() => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data?.map((resource): MultiSelectOption => {
        return {
          title: resource.name,
          value: resource.integrationGUID,
        };
      });
    }

    return [];
  }, [filteredResources.data, filteredResources.status]);

  const complexLossFilterOptions: Filter[] = useMemo(() => {
    let filters: Filter[] = [
      {
        id: 'complexResources',
        name: 'complexResources',
        label: 'Complex Loss Resources',
        type: 'async-select',
        multi: true,
        options: mappedFilteredResources,
        setInput: setComplexLossResourceFilterInput,
        loading: filteredResources?.status === 'loading',
        tagLimit: 10,
      },
      {
        id: 'teams',
        name: 'teams',
        label: 'Teams',
        type: 'select',
        multi: true,
        options: companyTeams || [],
      },
    ];

    if (isAdjuster) {
      filters = filters.filter((filter) => filter.id !== 'complexResources');
    }

    return filters;
  }, [
    companyTeams,
    filteredResources?.status,
    isAdjuster,
    mappedFilteredResources,
  ]);

  return (
    <PageComponent<ComplexLossFilters>
      searchPlaceholder="Claim Number"
      searchType="claimNumber"
      onSearch={onSearch}
      title="Complex Loss"
      dynamicFilters={{
        filters: [{ label: 'Filters', data: complexLossFilterOptions }],
      }}
    >
      <>
        <ComplexLossTreeView
          complexLossData={complexLossData as ComplexLossDataResponse}
          complexLossQuery={complexLossDataQuery}
          apiFilters={apiFilters}
          setApiFilters={setApiFilters}
        />
        <Outlet />
      </>
    </PageComponent>
  );
};
